import { useState } from 'react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { PiPlus, PiCalendarBlank, PiMegaphone } from 'react-icons/pi'

import { DataTableProvider } from 'components/DataTable'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
  FBPageInnerTabsContainer,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { TabPanel } from 'components/FbUI/PillTabs'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { cn } from 'components/UI/cn'
import {
  segmentedPillGroupClassSm,
  segmentedPillBaseClass,
  segmentedPillActiveClass,
  segmentedPillInactiveClass,
} from 'components/FbUI/segmentedPillButton'
import {
  DateRangePicker,
  type DateRangeValue,
} from 'pages/app/deals/DateRangePicker'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { useGetCampaignStats } from 'services/campaigns'
import { CampaignsStatsBar } from 'features/campaigns/CampaignsStatsBar'
import { CampaignsTable } from 'features/campaigns/CampaignsTable'
import { CampaignRepliesTab } from 'features/campaigns/CampaignRepliesTab'
import { CreateCampaignModalV2 } from 'features/campaigns/CreateCampaignModalV2'
import { CreateEditCampaignModal } from 'features/campaigns/CreateEditCampaignModal'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import { useGetReplyThreadSummary } from 'features/campaigns/replies_api'
import { Campaign } from 'models/campaign'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

type PageTab = 'campaigns' | 'replies'

const STATS_PERIOD_OPTIONS = [
  { key: 'all', label: 'All Time' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
] as const
type StatsPeriod = (typeof STATS_PERIOD_OPTIONS)[number]['key'] | 'custom'

const STATS_PILL_BASE = cn(segmentedPillBaseClass, 'gap-1.5')

export function CampaignsPage() {
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<PageTab>('campaigns')
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('all')
  const [statsDateRange, setStatsDateRange] =
    useState<DateRangeValue>(undefined)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showV2Modal, setShowV2Modal] = useState<boolean>(false)
  const [showV2EditModal, setShowV2EditModal] = useState<boolean>(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>()

  function handleRowEdit(campaign: Campaign) {
    setSelectedCampaign(campaign)
    // Sequencing campaigns edit through the V2 modal, which exposes owner +
    // notification recipients. The legacy modal has neither, so editing from
    // the list previously dropped both fields (FIR hotfix).
    if (featureFlag.enableEmailSequencing) {
      setShowV2EditModal(true)
    } else {
      setShowModal(true)
    }
  }

  function openModal() {
    if (featureFlag.enableEmailSequencing) {
      setShowV2Modal(true)
    } else {
      setShowModal(true)
    }
  }

  function hideModal() {
    setShowModal(false)
    setSelectedCampaign(undefined)
  }

  function hideV2EditModal() {
    setShowV2EditModal(false)
    setSelectedCampaign(undefined)
  }

  const { data: stats } = useGetCampaignStats(
    featureFlag.enableEmailSequencing,
    statsPeriod,
    statsDateRange?.from ? statsDateRange : undefined
  )
  const { data: replySummary } = useGetReplyThreadSummary()

  const customRangeLabel = statsDateRange?.from
    ? statsDateRange.to
      ? `${format(statsDateRange.from, 'd MMM')} – ${format(statsDateRange.to, 'd MMM')}`
      : format(statsDateRange.from, 'd MMM')
    : null

  if (!featureFlag.isLoading && !featureFlag.enableCampaigns) {
    navigate('/')
    return null
  }

  if (featureFlag.enableEmailSequencing) {
    return (
      <DataTableProvider tableKey="campaign-overview-table">
        <FBPage
          // Tighter outer padding than the global FBPage default (20px gray
          // gutter + 24px card padding ≈ 44px/edge) so the content uses more of
          // the viewport and the table gets more height. Scoped to this page via
          // the className overrides; the global --page-*-padding vars are
          // untouched, so every other page keeps the standard shell padding.
          innerPageClassName="!bg-surface-page !p-[8px]"
          containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
        >
          <FBPageHeader>
            <FBPageTitleRow>
              {/* Eyebrow ("Campaigns" + left-nav icon) over a tab-driven title,
                  matching the Targets / Contacts header pattern (and height).
                  The title tracks the active tab like Contacts' "All Contacts" /
                  "Contact Requests". */}
              <div className="flex flex-col">
                <span className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full bg-neutral-100 px-2.5 py-0.5 text-sm font-medium tracking-tight text-neutral-700">
                  <PiMegaphone size={14} />
                  Campaigns
                </span>
                <FBPageTitle className="!text-fg">
                  {activeTab === 'campaigns' ? 'All Campaigns' : 'All Replies'}
                </FBPageTitle>
              </div>
            </FBPageTitleRow>
            <FbButton
              onClick={() => openModal()}
              className="!bg-brand-bright !text-white hover:!bg-brand-active"
            >
              <PiPlus size={18} />
              New Campaign
            </FbButton>
          </FBPageHeader>

          <div className="pt-3 flex flex-col flex-1 min-h-0">
            {/* Tabs above the stats (mirrors the Deals / Contacts header order):
                tablist first, then the campaign-specific stats band below it. */}
            <UnderlineTabs
              tabs={[
                {
                  key: 'campaigns',
                  title: 'All Campaigns',
                  count: stats?.total_campaigns ?? null,
                },
                {
                  key: 'replies',
                  title: 'All Replies',
                  count:
                    replySummary?.is_available && replySummary.total != null
                      ? replySummary.total
                      : null,
                },
              ]}
              activeKey={activeTab}
              onChange={setActiveTab}
              idPrefix="campaigns-page"
              aria-label="Campaigns and replies"
              size="sm"
              // w-fit so the tablist (and its border-b divider) hugs the two
              // tabs instead of stretching the line across the full row.
              className="mb-3 shrink-0 w-fit"
            />
            {activeTab === 'campaigns' && (
              // Contained in its own card so it reads as a self-scoped summary:
              // the time-period toggle filters these stats only, NOT the table
              // below. Matches the table card's border/shadow as a sibling.
              <div className="mb-3 shrink-0 rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
                <div
                  // `flex w-fit` (overriding the group's inline-flex) drops the
                  // inline line-box whitespace below the pills, so the gap to the
                  // stats is exactly the mb — a clean 24px.
                  className={cn(
                    segmentedPillGroupClassSm,
                    'flex w-fit mb-[24px]'
                  )}
                  role="group"
                  aria-label="Stats time period"
                >
                  {STATS_PERIOD_OPTIONS.map((option) => {
                    const isActive = statsPeriod === option.key
                    return (
                      <button
                        key={option.key}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => {
                          setStatsPeriod(option.key)
                          setStatsDateRange(undefined)
                        }}
                        className={cn(
                          STATS_PILL_BASE,
                          isActive
                            ? segmentedPillActiveClass
                            : segmentedPillInactiveClass
                        )}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                  <DateRangePicker
                    value={statsDateRange}
                    onChange={(range) => {
                      setStatsDateRange(range)
                      setStatsPeriod('custom')
                    }}
                    renderTrigger={({ open }) => {
                      const isActive = statsPeriod === 'custom'
                      return (
                        <button
                          type="button"
                          aria-pressed={isActive}
                          className={cn(
                            STATS_PILL_BASE,
                            isActive || open
                              ? segmentedPillActiveClass
                              : segmentedPillInactiveClass
                          )}
                        >
                          <PiCalendarBlank size={16} />
                          {customRangeLabel
                            ? `Custom (${customRangeLabel})`
                            : 'Custom'}
                        </button>
                      )
                    }}
                  />
                </div>
                <CampaignsStatsBar stats={stats} />
              </div>
            )}

            <TabPanel<PageTab>
              idPrefix="campaigns-page"
              tabKey="replies"
              activeKey={activeTab}
              className="flex flex-col flex-1 min-h-0"
            >
              <CampaignRepliesTab />
            </TabPanel>

            <TabPanel<PageTab>
              idPrefix="campaigns-page"
              tabKey="campaigns"
              activeKey={activeTab}
              className="flex flex-col flex-1 min-h-0"
            >
              <TableLinkStyleProvider variant="plain">
                {/* xs toolbar density (28px) for the whole table (search +
                    Filter + the portaled Columns button) so it matches the
                    DS2026 size="sm" controls on the sibling Replies tab.
                    Wrapping here keeps the context above DataTable's
                    createPortal. */}
                <ToolbarDensityProvider value="xs">
                  <FBPageTableContainer className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
                    <CampaignsTable
                      onEditCampaign={handleRowEdit}
                      tableKey={ALL_CAMPAIGNS_TABLE_KEY}
                    />
                  </FBPageTableContainer>
                </ToolbarDensityProvider>
              </TableLinkStyleProvider>
            </TabPanel>
          </div>
        </FBPage>

        {selectedCampaign && (
          <EditCampaignModalV2
            show={showV2EditModal}
            onHide={hideV2EditModal}
            campaign={selectedCampaign}
            campaignId={selectedCampaign.id}
          />
        )}
        <CreateCampaignModalV2
          show={showV2Modal}
          onHide={() => setShowV2Modal(false)}
        />
      </DataTableProvider>
    )
  }

  const tabs: { key: PageTab; title: string | React.JSX.Element }[] = [
    { key: 'campaigns', title: 'Campaigns' },
    {
      key: 'replies',
      title: (
        <span>
          Replies
          {replySummary?.is_available && replySummary.total !== undefined && (
            <i className="font-extralight not-italic">
              {' '}
              ({replySummary.total})
            </i>
          )}
        </span>
      ),
    },
  ]

  return (
    <DataTableProvider tableKey="campaign-overview-table">
      <FBPage innerPageClassName="!bg-surface-page">
        <FBPageHeader>
          <FBPageTitleRow>
            <FBPageTitle>
              <span className={'font-normal'}>Campaigns</span>
            </FBPageTitle>
          </FBPageTitleRow>
          {activeTab === 'campaigns' && (
            <FbButton style={{ width: 200 }} onClick={() => openModal()}>
              Create Campaign
            </FbButton>
          )}
        </FBPageHeader>

        {featureFlag.enableEmailSequencing && (
          <FBPageInnerTabsContainer>
            <Tabs
              tabs={tabs}
              selectedTab={activeTab}
              setSelectedTab={setActiveTab}
              tabStyle={{ width: '120px' }}
            />
          </FBPageInnerTabsContainer>
        )}

        {activeTab === 'campaigns' && (
          <TableLinkStyleProvider variant="plain">
            <FBPageTableContainer
              className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
              style={{
                height: 'calc(var(--base-page-table-height) - 47px)',
              }}
            >
              <CampaignsTable
                onEditCampaign={handleRowEdit}
                tableKey={ALL_CAMPAIGNS_TABLE_KEY}
              />
            </FBPageTableContainer>
          </TableLinkStyleProvider>
        )}

        {activeTab === 'replies' && (
          <div style={{ height: 'calc(var(--base-page-table-height) - 60px)' }}>
            <CampaignRepliesTab />
          </div>
        )}
      </FBPage>
      <CreateEditCampaignModal
        isOpen={showModal}
        onClose={hideModal}
        editingCampaign={selectedCampaign}
      />
      <CreateCampaignModalV2
        show={showV2Modal}
        onHide={() => setShowV2Modal(false)}
      />
    </DataTableProvider>
  )
}
